import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AttachEmailOutlinedIcon from "@mui/icons-material/AttachEmailOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import {
  useGetContractsQuery,
  useGetContractInvoicesQuery,
  useGetAllApHistoryQuery,
} from "../../Api/attoDeskApi";
import { IContract, IContractInvoice } from "../../Api/Interface/api.interface";

dayjs.extend(isBetween);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v: number) =>
  (isNaN(v) || !isFinite(v) ? 0 : v).toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 2 });

const clientName = (c: IContract): string => {
  const cu = (c as any).client;
  if (!cu) return "—";
  return (
    cu.customerCompanyName ||
    `${cu.customerFirstName || ""} ${cu.customerLastName || ""}`.trim() ||
    "—"
  );
};

const billingColors: Record<string, { bg: string; color: string; border: string }> = {
  daily:   { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  weekly:  { bg: "#f5f3ff", color: "#2f3891", border: "#ddd6fe" },
  monthly: { bg: "#fdf4ff", color: "#9333ea", border: "#f0abfc" },
};

const STATUS_MAP: Record<number, { label: string; color: string; bg: string; border: string }> = {
  1: { label: "Draft",     color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" },
  2: { label: "Finalized", color: "#d97706", bg: "#fef3c7", border: "#fcd34d" },
  3: { label: "Paid",      color: "#16a34a", bg: "#dcfce7", border: "#86efac" },
};

function estimateMonthlyValue(contract: IContract): number {
  const itemSum = (contract.items || []).reduce((sum, item) => {
    const base = (parseFloat(String(item.unit_price)) || 0) * (parseFloat(String(item.buses)) || 1);
    return sum + base + base * ((parseFloat(String(item.tax_percent)) || 0) / 100);
  }, 0);
  switch (contract.billing_type) {
    case "daily":   return itemSum * 20; // ~20 working days/month
    case "weekly":  return itemSum * 4;
    case "monthly": return itemSum;
    default:        return itemSum;
  }
}

function isActiveContract(c: IContract, date: dayjs.Dayjs): boolean {
  if ((c as any).isDelete) return false;
  const s = dayjs(c.start_date);
  const e = dayjs(c.end_date);
  return (date.isAfter(s) || date.isSame(s, "day")) && (date.isBefore(e) || date.isSame(e, "day"));
}

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatCard = ({
  icon, label, value, sub, color, bg, border, loading,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color: string;
  bg: string;
  border: string;
  loading?: boolean;
}) => (
  <Box sx={{
    flex: "1 1 160px", bgcolor: "#fff", borderRadius: 3,
    border: `1px solid ${border}`, boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
    p: 2.25, display: "flex", alignItems: "flex-start", gap: 1.75,
    minHeight: 96,
    transition: "box-shadow 0.15s", "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.08)" },
  }}>
    <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Box sx={{ color, fontSize: 22, lineHeight: 0 }}>{icon}</Box>
    </Box>
    <Box>
      <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.25 }}>
        {label}
      </Typography>
      {loading ? (
        <CircularProgress size={16} sx={{ color, mt: 0.25 }} />
      ) : (
        <>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>{value}</Typography>
          {sub && <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>{sub}</Typography>}
        </>
      )}
    </Box>
  </Box>
);

const SectionHeader = ({ title, sub, action }: { title: string; sub?: string; action?: React.ReactNode }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>{title}</Typography>
      {sub && <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.2 }}>{sub}</Typography>}
    </Box>
    {action}
  </Box>
);

// ─── Forecast bar chart (pure CSS) ────────────────────────────────────────────
const ForecastBar = ({ month, value, maxValue, isCurrent, isCollected }: {
  month: string; value: number; maxValue: number; isCurrent: boolean; isCollected?: boolean;
}) => {
  const pct = maxValue > 0 ? Math.max(4, (value / maxValue) * 100) : 4;
  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, minWidth: 70 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: isCurrent ? "#2f3891" : "#0f172a" }}>
        {fmt(value)}
      </Typography>
      <Box sx={{ width: "100%", height: 110, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
        <Box sx={{
          width: "65%", height: `${pct}%`,
          borderRadius: "6px 6px 0 0",
          background: isCurrent
            ? "linear-gradient(180deg, #2f3891, #2f3891)"
            : isCollected
            ? "linear-gradient(180deg, #22c55e, #16a34a)"
            : "linear-gradient(180deg, #c7d2fe, #a5b4fc)",
          boxShadow: isCurrent ? "0 4px 12px rgba(99,102,241,0.35)" : "none",
          transition: "height 0.4s ease",
        }} />
      </Box>
      <Typography sx={{ fontSize: 11.5, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? "#2f3891" : "#64748b" }}>
        {month}
      </Typography>
      {isCurrent && (
        <Chip label="Current" size="small"
          sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: "#eef2ff", color: "#2f3891", border: "1px solid #c7d2fe" }} />
      )}
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ContractBillingDashboard: React.FC = () => {
  const navigate  = useNavigate();
  const today     = dayjs();
  const thisMonth = today.format("YYYY-MM");

  // ── Data fetch ───────────────────────────────────────────────────────────
  const {
    data: contractsData,
    isFetching: contractsLoading,
    refetch: refetchContracts,
  } = useGetContractsQuery({});

  const {
    data: invoicesData,
    isFetching: invoicesLoading,
    refetch: refetchInvoices,
  } = useGetContractInvoicesQuery({});

  const {
    data: apHistoryData,
  } = useGetAllApHistoryQuery();

  const loading = contractsLoading || invoicesLoading;

  // ── Derived data ─────────────────────────────────────────────────────────
  const allContracts: IContract[] = useMemo(() => {
    if (!Array.isArray(contractsData?.data)) return [];
    return contractsData!.data as IContract[];
  }, [contractsData]);

  const activeContracts = useMemo(
    () => allContracts.filter((c) => isActiveContract(c, today)),
    [allContracts, today]
  );

  const allInvoices: IContractInvoice[] = useMemo(() => {
    if (!Array.isArray(invoicesData?.data)) return [];
    return invoicesData!.data as IContractInvoice[];
  }, [invoicesData]);

  const thisMonthInvoices = useMemo(
    () => allInvoices.filter((inv) => inv.issue_date?.startsWith(thisMonth)),
    [allInvoices, thisMonth]
  );

  // Map: client_id → most recent invoice this month
  const invoiceByClientThisMonth = useMemo(() => {
    const map: Record<string, IContractInvoice> = {};
    for (const inv of thisMonthInvoices) {
      const key = String(inv.customer_id);
      if (!map[key] || dayjs(inv.created_at).isAfter(dayjs(map[key].created_at))) {
        map[key] = inv;
      }
    }
    return map;
  }, [thisMonthInvoices]);

  // ── Summary stats ────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const collected   = allInvoices.reduce((s, i) => s + (i.amount_paid || 0), 0);
    const outstanding = allInvoices.reduce((s, i) => s + Math.max(0, (i.due_amount || 0)), 0);
    const expected    = thisMonthInvoices.reduce((s, i) => s + (i.grand_total || 0), 0);
    const apLogs      = Array.isArray(apHistoryData?.data) ? (apHistoryData!.data as any[]) : [];
    const recentAP    = new Set(apLogs.filter((l: any) => dayjs(l.created_at).isAfter(today.subtract(30, "day"))).map((l: any) => String(l.customer_id)));
    const pendingAP   = activeContracts.filter((c) => !recentAP.has(String(c.client_id))).length;
    return { collected, outstanding, expected, pendingAP };
  }, [allInvoices, thisMonthInvoices, activeContracts, apHistoryData, today]);

  // ── Forecast: next 4 months ───────────────────────────────────────────────
  const forecast = useMemo(() => {
    return [0, 1, 2, 3].map((offset) => {
      const m        = today.add(offset, "month");
      const key      = m.format("YYYY-MM");
      const existing = allInvoices
        .filter((inv) => inv.issue_date?.startsWith(key))
        .reduce((s, i) => s + (i.grand_total || 0), 0);
      const fromContracts = activeContracts
        .filter((c) => {
          const s = dayjs(c.start_date);
          const e = dayjs(c.end_date);
          return m.isBetween(s, e, "month", "[]");
        })
        .reduce((s, c) => s + estimateMonthlyValue(c), 0);
      return {
        month: m.format("MMM YY"),
        value: offset === 0 ? Math.max(existing, fromContracts) : fromContracts,
        collected: offset === 0 ? allInvoices.filter((inv) => inv.issue_date?.startsWith(key)).reduce((s, i) => s + (i.amount_paid || 0), 0) : 0,
        isCurrent: offset === 0,
      };
    });
  }, [today, allInvoices, activeContracts]);

  const forecastMax = useMemo(() => Math.max(...forecast.map((f) => f.value), 1), [forecast]);

  // ── Per-client performance ────────────────────────────────────────────────
  const clientPerformance = useMemo(() => {
    const map: Record<string, { name: string; contractValue: number; invoiced: number; collected: number; outstanding: number; contracts: number }> = {};

    for (const c of activeContracts) {
      const key  = String(c.client_id);
      const name = clientName(c);
      if (!map[key]) map[key] = { name, contractValue: 0, invoiced: 0, collected: 0, outstanding: 0, contracts: 0 };
      map[key].contractValue += estimateMonthlyValue(c);
      map[key].contracts++;
    }

    for (const inv of allInvoices) {
      const key = String(inv.customer_id);
      if (!map[key]) {
        const cu   = (inv as any).customer;
        const name = cu?.customerCompanyName || `${cu?.customerFirstName || ""} ${cu?.customerLastName || ""}`.trim() || "—";
        map[key] = { name, contractValue: 0, invoiced: 0, collected: 0, outstanding: 0, contracts: 0 };
      }
      map[key].invoiced     += inv.grand_total || 0;
      map[key].collected    += inv.amount_paid || 0;
      map[key].outstanding  += Math.max(0, inv.due_amount || 0);
    }

    return Object.values(map).sort((a, b) => b.invoiced - a.invoiced);
  }, [activeContracts, allInvoices]);

  // ── AP history by client ──────────────────────────────────────────────────
  const apByClient = useMemo(() => {
    const logs = Array.isArray(apHistoryData?.data) ? (apHistoryData!.data as any[]) : [];
    const map: Record<string, { lastSent: string; count: number }> = {};
    for (const log of logs) {
      const key = String(log.customer_id);
      if (!map[key] || dayjs(log.created_at).isAfter(dayjs(map[key].lastSent))) {
        map[key] = { lastSent: log.created_at, count: (map[key]?.count || 0) + 1 };
      } else {
        map[key].count = (map[key].count || 0) + 1;
      }
    }
    return map;
  }, [apHistoryData]);

  const handleRefresh = () => {
    refetchContracts();
    refetchInvoices();
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>

      {/* ── Page Header ───────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 42, height: 42, borderRadius: 2.5, background: "linear-gradient(135deg,#2f3891,#2f3891)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <TrendingUpIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20, color: "#0f172a", lineHeight: 1 }}>
              Contract Billing Dashboard
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.3 }}>
              {today.format("MMMM YYYY")} · Recurring revenue control center
            </Typography>
          </Box>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Tooltip title="Refresh all data">
            <IconButton size="small" onClick={handleRefresh} disabled={loading}
              sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, bgcolor: "#fff", "&:hover": { bgcolor: "#f1f5f9" } }}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            onClick={() => navigate("/contract-invoice/create")}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12.5, fontWeight: 700, borderColor: "#e2e8f0", color: "#475569", "&:hover": { borderColor: "#2f3891", color: "#2f3891" } }}>
            New Invoice
          </Button>
          <Button size="small" startIcon={<AttachEmailOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={() => navigate("/ap/email")}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12.5, fontWeight: 700, borderColor: "#e2e8f0", color: "#475569", "&:hover": { borderColor: "#2f3891", color: "#2f3891" } }}>
            AP Email
          </Button>
          <Button size="small" startIcon={<ArticleOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={() => navigate("/contract-invoice/list")}
            variant="contained"
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12.5, fontWeight: 700, bgcolor: "#2f3891", "&:hover": { bgcolor: "#2f3891" } }}>
            All Invoices
          </Button>
        </Box>
      </Box>

      {/* ════════════════════════════════════════════════════════════════════
          1. SUMMARY STAT CARDS
      ════════════════════════════════════════════════════════════════════ */}
      <Box sx={{ display: "flex", gap: 1.75, flexWrap: "wrap", alignItems: "stretch", mb: 3 }}>
        <StatCard
          icon={<GroupsOutlinedIcon fontSize="inherit" />}
          label="Active Contracts"
          value={String(activeContracts.length)}
          sub={`${allContracts.length} total contracts`}
          color="#2f3891" bg="#eef2ff" border="#c7d2fe"
          loading={contractsLoading}
        />
        <StatCard
          icon={<TrendingUpIcon fontSize="inherit" />}
          label="Expected This Month"
          value={fmt(stats.expected)}
          sub={`${thisMonthInvoices.length} invoice${thisMonthInvoices.length !== 1 ? "s" : ""} generated`}
          color="#2563eb" bg="#eff6ff" border="#bfdbfe"
          loading={invoicesLoading}
        />
        <StatCard
          icon={<ReceiptLongOutlinedIcon fontSize="inherit" />}
          label="Generated (This Month)"
          value={String(thisMonthInvoices.length)}
          sub={`of ${activeContracts.length} active contracts`}
          color="#2f3891" bg="#f5f3ff" border="#ddd6fe"
          loading={invoicesLoading}
        />
        <StatCard
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          label="Collected Revenue"
          value={fmt(stats.collected)}
          sub="All time payments received"
          color="#16a34a" bg="#dcfce7" border="#86efac"
          loading={invoicesLoading}
        />
        <StatCard
          icon={<AccountBalanceWalletOutlinedIcon fontSize="inherit" />}
          label="Outstanding"
          value={fmt(stats.outstanding)}
          sub="Unpaid balance"
          color={stats.outstanding > 0 ? "#dc2626" : "#16a34a"}
          bg={stats.outstanding > 0 ? "#fef2f2" : "#dcfce7"}
          border={stats.outstanding > 0 ? "#fecaca" : "#86efac"}
          loading={invoicesLoading}
        />
        <StatCard
          icon={<PendingActionsOutlinedIcon fontSize="inherit" />}
          label="Pending AP Emails"
          value={String(stats.pendingAP)}
          sub="Clients not contacted (30d)"
          color={stats.pendingAP > 0 ? "#d97706" : "#16a34a"}
          bg={stats.pendingAP > 0 ? "#fef3c7" : "#dcfce7"}
          border={stats.pendingAP > 0 ? "#fcd34d" : "#86efac"}
          loading={contractsLoading}
        />
      </Box>

      {/* ════════════════════════════════════════════════════════════════════
          2. MONTHLY CONTRACT BILLING TABLE  +  3. REVENUE FORECAST
      ════════════════════════════════════════════════════════════════════ */}
      <Box sx={{ display: "flex", gap: 2.5, mb: 3, flexWrap: "wrap" }}>

        {/* ── Monthly Billing ──────────────────────────────────────────── */}
        <Box sx={{ flex: "2 1 480px", minWidth: 0 }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
                  Monthly Contract Billing
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.15 }}>
                  {today.format("MMMM YYYY")} · {activeContracts.length} active contracts
                </Typography>
              </Box>
              <Button size="small" startIcon={<AutorenewIcon sx={{ fontSize: 14 }} />}
                onClick={() => navigate("/contract-invoice/create")}
                sx={{ borderRadius: 2, textTransform: "none", fontSize: 11.5, fontWeight: 700, bgcolor: "#2f3891", color: "#fff", px: 1.5, "&:hover": { bgcolor: "#2f3891" } }}>
                Generate All
              </Button>
            </Box>

            {contractsLoading ? (
              <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} sx={{ color: "#2f3891" }} /></Box>
            ) : activeContracts.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <ArticleOutlinedIcon sx={{ fontSize: 40, color: "#e2e8f0", mb: 1 }} />
                <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>No active contracts this month.</Typography>
                <Button size="small" onClick={() => navigate("/contract-invoice/create")} sx={{ mt: 1, textTransform: "none", fontSize: 12, color: "#2f3891" }}>
                  Create a contract →
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      {["Client", "Contract", "Billing", "Est. Amount", "Status", "Actions"].map((h) => (
                        <TableCell key={h} sx={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.25, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeContracts.map((contract) => {
                      const name      = clientName(contract);
                      const estimated = estimateMonthlyValue(contract);
                      const billing   = billingColors[contract.billing_type] || billingColors.monthly;
                      const inv       = invoiceByClientThisMonth[String(contract.client_id)];
                      const invStatus = inv ? (STATUS_MAP[inv.status] ?? STATUS_MAP[1]) : null;

                      return (
                        <TableRow key={contract.id}
                          sx={{ "&:hover": { bgcolor: "#f8f9ff" }, "&:last-child td": { border: 0 } }}>
                          <TableCell sx={{ py: 1.25, borderBottom: "1px solid #f8fafc" }}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>{name}</Typography>
                            <Typography sx={{ fontSize: 10.5, color: "#94a3b8" }}>
                              {dayjs(contract.start_date).format("MMM D")} – {dayjs(contract.end_date).format("MMM D, YYYY")}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.25, borderBottom: "1px solid #f8fafc" }}>
                            <Typography sx={{ fontSize: 12, color: "#475569", fontWeight: 500 }}>
                              {contract.contract_name}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.25, borderBottom: "1px solid #f8fafc" }}>
                            <Chip label={contract.billing_type.charAt(0).toUpperCase() + contract.billing_type.slice(1)}
                              size="small"
                              sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: billing.bg, color: billing.color, border: `1px solid ${billing.border}` }} />
                          </TableCell>
                          <TableCell sx={{ py: 1.25, borderBottom: "1px solid #f8fafc" }}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                              {fmt(estimated)}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>/ month est.</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.25, borderBottom: "1px solid #f8fafc" }}>
                            {invStatus ? (
                              <Chip label={invStatus.label} size="small"
                                sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: invStatus.bg, color: invStatus.color }} />
                            ) : (
                              <Chip label="Not Generated" size="small"
                                sx={{ height: 20, fontSize: 10.5, fontWeight: 600, bgcolor: "#f1f5f9", color: "#94a3b8" }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 1.25, borderBottom: "1px solid #f8fafc" }}>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Tooltip title={inv ? "View Invoice" : "Generate Invoice"}>
                                <Button size="small"
                                  onClick={() => inv
                                    ? navigate(`/contract-invoice/edit/${inv.id}`)
                                    : navigate(`/contract-invoice/create/from-contract/${contract.client_id}/${contract.id}`)
                                  }
                                  sx={{ minWidth: 0, px: 0.875, py: 0.375, borderRadius: 1.5, fontSize: 10.5, fontWeight: 700, textTransform: "none",
                                    bgcolor: inv ? "#eff6ff" : "#2f3891", color: inv ? "#2563eb" : "#fff",
                                    "&:hover": { bgcolor: inv ? "#dbeafe" : "#2f3891" } }}>
                                  {inv ? "View" : "Generate"}
                                </Button>
                              </Tooltip>
                              <Tooltip title="Send AP Email">
                                <Button size="small"
                                  onClick={() => navigate("/ap/email")}
                                  sx={{ minWidth: 0, px: 0.875, py: 0.375, borderRadius: 1.5, fontSize: 10.5, fontWeight: 700, textTransform: "none",
                                    bgcolor: "#fef3c7", color: "#d97706", "&:hover": { bgcolor: "#fde68a" } }}>
                                  AP
                                </Button>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>

        {/* ── Revenue Forecast ─────────────────────────────────────────── */}
        <Box sx={{ flex: "1 1 240px", minWidth: 220 }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.05)", height: "100%" }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #f1f5f9" }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Revenue Forecast</Typography>
              <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.15 }}>Next 4 months projected</Typography>
            </Box>
            <Box sx={{ p: 2.5 }}>
              {/* Bars */}
              <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, mb: 2 }}>
                {forecast.map((f) => (
                  <ForecastBar
                    key={f.month}
                    month={f.month}
                    value={f.value}
                    maxValue={forecastMax}
                    isCurrent={f.isCurrent}
                    isCollected={false}
                  />
                ))}
              </Box>

              {/* Legend */}
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: 1, background: "linear-gradient(#2f3891,#2f3891)" }} />
                  <Typography sx={{ fontSize: 10.5, color: "#64748b" }}>Current month</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: "#c7d2fe" }} />
                  <Typography sx={{ fontSize: 10.5, color: "#64748b" }}>Projected</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 1.75 }} />

              {/* Forecast summary rows */}
              {forecast.map((f, i) => (
                <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.625,
                  borderBottom: i < forecast.length - 1 ? "1px solid #f8fafc" : "none" }}>
                  <Typography sx={{ fontSize: 12, color: f.isCurrent ? "#2f3891" : "#475569", fontWeight: f.isCurrent ? 700 : 500 }}>
                    {f.month}
                    {f.isCurrent && <Typography component="span" sx={{ fontSize: 10, color: "#94a3b8", ml: 0.5 }}>(current)</Typography>}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>{fmt(f.value)}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* ════════════════════════════════════════════════════════════════════
          4. CONTRACT PERFORMANCE TABLE
      ════════════════════════════════════════════════════════════════════ */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.05)", mb: 3 }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Contract Performance</Typography>
            <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.15 }}>Financial breakdown by client (all time)</Typography>
          </Box>
          <Button size="small" endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
            onClick={() => navigate("/contract-invoice/list")}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 11.5, fontWeight: 600, color: "#2f3891", "&:hover": { bgcolor: "#eef2ff" } }}>
            View All Invoices
          </Button>
        </Box>

        {invoicesLoading || contractsLoading ? (
          <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={22} sx={{ color: "#2f3891" }} /></Box>
        ) : clientPerformance.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>No data yet. Generate some invoices to see performance.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  {["Client", "Est. Monthly Value", "Total Invoiced", "Collected", "Outstanding", ""].map((h) => (
                    <TableCell key={h} sx={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.25, borderBottom: "1px solid #f1f5f9" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {clientPerformance.map((row, idx) => {
                  const collectionRate = row.invoiced > 0 ? (row.collected / row.invoiced) * 100 : 0;
                  return (
                    <TableRow key={idx} sx={{ "&:hover": { bgcolor: "#f8f9ff" }, "&:last-child td": { border: 0 } }}>
                      <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f8fafc" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                          <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#2f3891" }}>
                              {row.name.charAt(0).toUpperCase()}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>{row.name}</Typography>
                            {row.contracts > 0 && (
                              <Typography sx={{ fontSize: 10.5, color: "#94a3b8" }}>
                                {row.contracts} active contract{row.contracts !== 1 ? "s" : ""}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f8fafc" }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#475569" }}>
                          {row.contractValue > 0 ? fmt(row.contractValue) : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f8fafc" }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                          {fmt(row.invoiced)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f8fafc" }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#16a34a" }}>
                          {fmt(row.collected)}
                        </Typography>
                        {row.invoiced > 0 && (
                          <Box sx={{ mt: 0.5, height: 4, bgcolor: "#f1f5f9", borderRadius: 2, overflow: "hidden", width: 80 }}>
                            <Box sx={{ height: "100%", width: `${Math.min(100, collectionRate)}%`, bgcolor: "#22c55e", borderRadius: 2 }} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f8fafc" }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: row.outstanding > 0 ? "#dc2626" : "#16a34a" }}>
                          {fmt(row.outstanding)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f8fafc" }}>
                        <Button size="small"
                          onClick={() => navigate("/contract-invoice/create")}
                          sx={{ minWidth: 0, px: 1, py: 0.375, borderRadius: 1.5, fontSize: 10.5, fontWeight: 700, textTransform: "none",
                            color: "#2f3891", "&:hover": { bgcolor: "#eef2ff" } }}>
                          Invoice
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ════════════════════════════════════════════════════════════════════
          5. AP EMAIL STATUS
      ════════════════════════════════════════════════════════════════════ */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.05)", mb: 3 }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>AP Email Status</Typography>
            <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.15 }}>
              Last contact status for each active client
            </Typography>
          </Box>
          <Button size="small" startIcon={<AttachEmailOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => navigate("/ap/email")}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 11.5, fontWeight: 700, bgcolor: "#fffbeb", color: "#d97706",
              border: "1px solid #fcd34d", "&:hover": { bgcolor: "#fef3c7" } }}>
            Open AP Email
          </Button>
        </Box>

        {contractsLoading ? (
          <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={22} sx={{ color: "#2f3891" }} /></Box>
        ) : activeContracts.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>No active contracts to track AP status for.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  {["Client", "Contract", "Last AP Sent", "Status", "Action"].map((h) => (
                    <TableCell key={h} sx={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.25, borderBottom: "1px solid #f1f5f9" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {activeContracts.map((contract) => {
                  const key       = String(contract.client_id);
                  const apInfo    = apByClient[key];
                  const lastSent  = apInfo?.lastSent ? dayjs(apInfo.lastSent) : null;
                  const daysAgo   = lastSent ? today.diff(lastSent, "day") : null;
                  const isRecent  = daysAgo !== null && daysAgo <= 7;
                  const isStale   = daysAgo !== null && daysAgo > 30;
                  const notSent   = !lastSent;

                  const statusChip = notSent
                    ? { label: "Never Sent", color: "#dc2626", bg: "#fef2f2" }
                    : isRecent
                    ? { label: "Sent Recently", color: "#16a34a", bg: "#dcfce7" }
                    : isStale
                    ? { label: "Overdue", color: "#d97706", bg: "#fef3c7" }
                    : { label: `Sent ${daysAgo}d ago`, color: "#2563eb", bg: "#eff6ff" };

                  return (
                    <TableRow key={contract.id}
                      sx={{ "&:hover": { bgcolor: "#f8f9ff" }, "&:last-child td": { border: 0 } }}>
                      <TableCell sx={{ py: 1.25, borderBottom: "1px solid #f8fafc" }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                          {clientName(contract)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.25, borderBottom: "1px solid #f8fafc" }}>
                        <Typography sx={{ fontSize: 12, color: "#475569" }}>{contract.contract_name}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.25, borderBottom: "1px solid #f8fafc" }}>
                        <Typography sx={{ fontSize: 12, color: lastSent ? "#475569" : "#94a3b8" }}>
                          {lastSent ? lastSent.format("MMM D, YYYY") : "—"}
                        </Typography>
                        {lastSent && (
                          <Typography sx={{ fontSize: 10.5, color: "#94a3b8" }}>{lastSent.format("h:mm A")}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.25, borderBottom: "1px solid #f8fafc" }}>
                        <Chip label={statusChip.label} size="small"
                          sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: statusChip.bg, color: statusChip.color }} />
                      </TableCell>
                      <TableCell sx={{ py: 1.25, borderBottom: "1px solid #f8fafc" }}>
                        <Button size="small"
                          onClick={() => navigate("/ap/email")}
                          sx={{ minWidth: 0, px: 1, py: 0.375, borderRadius: 1.5, fontSize: 10.5, fontWeight: 700, textTransform: "none",
                            bgcolor: "#fef3c7", color: "#d97706", "&:hover": { bgcolor: "#fde68a" } }}>
                          Send AP
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ════════════════════════════════════════════════════════════════════
          6. BOTTOM QUICK ACTIONS
      ════════════════════════════════════════════════════════════════════ */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", p: 2.5, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#0f172a", mb: 1.75 }}>Quick Actions</Typography>
        <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
          {[
            { label: "Generate Invoice",    icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />, path: "/contract-invoice/create",  color: "#2f3891", bg: "#eef2ff", border: "#c7d2fe" },
            { label: "Send AP Emails",      icon: <AttachEmailOutlinedIcon sx={{ fontSize: 16 }} />,  path: "/ap/email",                 color: "#d97706", bg: "#fffbeb", border: "#fcd34d" },
            { label: "View All Invoices",   icon: <ArticleOutlinedIcon sx={{ fontSize: 16 }} />,      path: "/contract-invoice/list",    color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
            { label: "Invoice Dashboard",   icon: <TrendingUpIcon sx={{ fontSize: 16 }} />,            path: "/invoice/list",             color: "#2f3891", bg: "#f5f3ff", border: "#ddd6fe" },
          ].map(({ label, icon, path, color, bg, border }) => (
            <Button key={label} startIcon={icon}
              onClick={() => navigate(path)}
              sx={{ borderRadius: 2.5, textTransform: "none", fontSize: 12.5, fontWeight: 700,
                bgcolor: bg, color, border: `1px solid ${border}`, px: 2, py: 1,
                "&:hover": { filter: "brightness(0.96)", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" } }}>
              {label}
            </Button>
          ))}
        </Box>
      </Paper>

    </Box>
  );
};

export default ContractBillingDashboard;
